/**
 * "Offene Hinweise" — the reconciliation's warning categories, one row each.
 *
 * The overview shows COUNTS, not the individual rows. The full per-invoice
 * detail stays where it already is and moves to its own page in a later slice;
 * an overview that lists every duplicate-risk document is the long page this
 * redesign exists to replace.
 *
 * Rows are informational, deliberately. The design reference puts a chevron on
 * each one and a "show all" link underneath, but there is no detail route to
 * send anyone to yet. A chevron that navigates nowhere is worse than no chevron,
 * so neither is rendered until the target exists.
 *
 * Categories with a count of zero are hidden: a list of things that are fine is
 * not a list of open items, and it buries the ones that are not.
 */
import React, { useState } from "react";
import { AlertTriangle, ChevronRight, CircleAlert, CheckCircle2 } from "lucide-react";

import BexioWarningDetailDrawer from "./BexioWarningDetailDrawer";

/**
 * The reconciliation warning buckets, in the order they matter.
 *
 * Keys are the response's own field names — every one of these is an array the
 * backend already returns, so nothing here can show a category that does not
 * exist. `severity` drives colour AND the icon, so the state is never carried
 * by colour alone.
 */
const CATEGORIES = [
  {
    key: "canceled_with_payment_rows",
    label: "Stornierte Rechnungen mit Zahlungszeilen",
    severity: "bad",
  },
  {
    key: "total_paid_differs_from_payment_sum",
    label: "total_paid weicht von Zahlungssumme ab",
    severity: "warn",
  },
  {
    key: "paid_without_payment_rows",
    label: "Bezahlt ohne Zahlungszeilen",
    severity: "warn",
  },
  {
    key: "pending_with_payment_rows",
    label: "Ausstehend mit Zahlungszeilen",
    severity: "warn",
  },
  {
    key: "excluded_mapped_invoices",
    label: "Ausgeschlossene zugeordnete Rechnungen",
    severity: "warn",
  },
  {
    key: "invalid_mapped_invoices",
    label: "Zuordnung nicht auflösbar",
    severity: "bad",
  },
  {
    key: "duplicate_risk_vantio_invoices",
    label: "Duplikat-Risiko (Vantio vs. Bexio)",
    severity: "warn",
  },
];

const SEVERITY = {
  bad: {
    Icon: CircleAlert,
    icon: "text-red-600 dark:text-red-400",
    badge:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
    sr: "Fehler",
  },
  warn: {
    Icon: AlertTriangle,
    icon: "text-amber-600 dark:text-amber-400",
    badge:
      "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    sr: "Warnung",
  },
};

export default function BexioWarningsCard({ reconciliation }) {
  const warnings = reconciliation?.warnings;
  // Which category the drawer is showing; null means closed.
  const [openCategory, setOpenCategory] = useState(null);

  const rows = CATEGORIES.map((c) => ({
    ...c,
    count: Array.isArray(warnings?.[c.key]) ? warnings[c.key].length : 0,
  })).filter((c) => c.count > 0);

  return (
    <section
      aria-labelledby="bexio-warnings-title"
      className="rounded-xl border border-border bg-card p-5"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="bexio-warnings-title" className="text-sm font-semibold text-foreground">
          Offene Hinweise
        </h2>
        {rows.length > 0 ? (
          <span className="shrink-0 text-xs text-muted-foreground">
            {rows.length} {rows.length === 1 ? "Kategorie" : "Kategorien"}
          </span>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2
            aria-hidden="true"
            className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
          />
          Keine offenen Hinweise.
        </div>
      ) : (
        /* Capped height with an internal scroll: all seven categories can be
           open at once, and letting that stretch the row makes the two cards
           beside it grow empty space to match. `pr-1` keeps the scrollbar off
           the row borders. The count above the list tells the reader there is
           more below the fold — a scroll container gives no other hint. */
        <ul
          tabIndex={0}
          aria-label="Offene Hinweise nach Kategorie"
          className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {rows.map((r) => {
            const tone = SEVERITY[r.severity] || SEVERITY.warn;
            const { Icon } = tone;
            return (
              <li key={r.key}>
                {/* A real button, not a clickable div: this opens a dialog, so it
                    has to be reachable and operable from the keyboard, and the
                    accessible name has to carry the count as well as the label —
                    a screen reader hears "3 Stornierte Rechnungen …", not the
                    number as a detached badge. */}
                <button
                  type="button"
                  onClick={() => setOpenCategory(r)}
                  aria-label={`${r.label}: ${r.count} ${
                    r.count === 1 ? "Eintrag" : "Einträge"
                  } anzeigen`}
                  className="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Icon aria-hidden="true" className={`h-4 w-4 shrink-0 ${tone.icon}`} />
                  <span className="sr-only">{tone.sr}:</span>
                  <span
                    aria-hidden="true"
                    className={`inline-flex min-w-[1.75rem] justify-center rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${tone.badge}`}
                  >
                    {r.count}
                  </span>
                  <span aria-hidden="true" className="min-w-0 flex-1 text-sm text-foreground">
                    {r.label}
                  </span>
                  <ChevronRight
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* The rows the count refers to. Fed straight from the response array the
          count was measured on, so the two can never describe different sets. */}
      <BexioWarningDetailDrawer
        open={openCategory != null}
        onOpenChange={(next) => {
          if (!next) setOpenCategory(null);
        }}
        category={openCategory}
        items={openCategory ? warnings?.[openCategory.key] : []}
        currency={reconciliation?.summary?.currency}
      />
    </section>
  );
}
